// frontend/src/components/pages/LsOverseasAccountPageDev.js

import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";

function todayKstYmd() {
  const kst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
  const y = kst.getFullYear();
  const m = String(kst.getMonth() + 1).padStart(2, "0");
  const d = String(kst.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function ymdToYyyymmdd(ymd) {
  return String(ymd || "").replaceAll("-", "");
}

function formatNumber(v, digits = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);

  return n.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatPlain(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function formatLsDateTime(v) {
  if (v === null || v === undefined || v === "") return "—";

  const s = String(v).trim();

  // 예: 20260519003241495 = YYYYMMDDHHmmssSSS
  if (/^\d{17}$/.test(s)) {
    const y = s.slice(0, 4);
    const mo = s.slice(4, 6);
    const d = s.slice(6, 8);
    const h = s.slice(8, 10);
    const mi = s.slice(10, 12);
    const sec = s.slice(12, 14);
    const ms = s.slice(14, 17);

    return `${y}-${mo}-${d} ${h}:${mi}:${sec}.${ms}`;
  }

  // 예: 20260519003241 = YYYYMMDDHHmmss
  if (/^\d{14}$/.test(s)) {
    const y = s.slice(0, 4);
    const mo = s.slice(4, 6);
    const d = s.slice(6, 8);
    const h = s.slice(8, 10);
    const mi = s.slice(10, 12);
    const sec = s.slice(12, 14);

    return `${y}-${mo}-${d} ${h}:${mi}:${sec}`;
  }

  // 예: 20260519 = YYYYMMDD
  if (/^\d{8}$/.test(s)) {
    const y = s.slice(0, 4);
    const mo = s.slice(4, 6);
    const d = s.slice(6, 8);

    return `${y}-${mo}-${d}`;
  }

  return s;
}

function pick(row, keys, fallback = "—") {
  if (!row || typeof row !== "object") return fallback;
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
      return row[key];
    }
  }
  return fallback;
}

function SectionTable({ title, columns, rows, emptyText = "데이터가 없습니다." }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
        <h2 className="text-base font-bold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-500">{rows?.length || 0}건</span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="whitespace-nowrap px-3 py-2 text-center font-semibold text-gray-800">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!rows || rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-4 text-center text-gray-400">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.id ?? `${title}-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`whitespace-nowrap px-3 py-2 ${
                        col.align === "right" ? "text-right" : col.align === "left" ? "text-left" : "text-center"
                      }`}
                    >
                      {col.render ? col.render(row, idx) : formatPlain(row[col.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function LsOverseasAccountPageDev() {
  const API = process.env.REACT_APP_API_URL;
  const SYMBOL = process.env.SYMBOL;

  const [isuCode, setIsuCode] = useState("MNQZ26");
  const [startDate, setStartDate] = useState(todayKstYmd());
  const [endDate, setEndDate] = useState(todayKstYmd());

  const [data, setData] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [cooldown, setCooldown] = useState(false);

  const cooldownTimerRef = useRef(null);

  const params = useMemo(() => {
    return {
      isu_code: isuCode.trim(),

      // 화면에서는 숨기지만 CIDBQ01400 조회를 위해 기본값으로 전달
      side: "BUY",
      ord_type: "MKT",
      price: 0,

      // 별도 거래일자 input 없이 시작일 기준으로 사용
      trade_date: ymdToYyyymmdd(startDate),

      // 체결내역은 시작일~종료일 범위조회로 고정
      today: false,
      start_date: ymdToYyyymmdd(startDate),
      end_date: ymdToYyyymmdd(endDate),
    };
  }, [isuCode, startDate, endDate]);

  async function fetchSummary() {
    if (loading || cooldown) return;

    if (!params.isu_code) {
      setErrMsg("종목코드를 입력해야 합니다.");
      return;
    }

    setLoading(true);
    setCooldown(true);
    setErrMsg("");

    try {
      const res = await axios.get(`${API}/ls-overseas-account/summary`, {
        params,
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      setErrMsg(
        typeof detail === "string" ? detail : detail?.message || e.message || "LS 계좌 정보를 불러오지 못했습니다.",
      );
      setData(null);
    } finally {
      setLoading(false);

      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }

      cooldownTimerRef.current = setTimeout(() => {
        setCooldown(false);

        cooldownTimerRef.current = null;
      }, 2000);
    }
  }

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  const accountStatusRows = data?.account_status || [];
  const orderableRows = data?.orderable_qty || [];
  const unsettledSummary = data?.unsettled_balance || [];
  const unsettledDetailRows = data?.unsettled_balance?.[0]?.rows || [];
  const orderFillDetailRows = data?.order_fill_detail || [];
  const dailyUnsettledRows = data?.daily_unsettled_balance || [];
  const depositAssetSummaryRows = data?.deposit_asset_summary || [];
  const depositAssetByCurrencyRows = data?.deposit_asset_by_currency || [];

  const accountStatusColumns = [
    {
      key: "ovrs_futs_dps",
      header: "현재 해외선물 예수금 정보",
      render: (r) => formatNumber(r.ovrs_futs_dps),
    },
    {
      key: "eval_asset_amt",
      header: "평가 자산 금액",
      render: (r) => formatNumber(r.eval_asset_amt),
    },
    {
      key: "ord_able_amt",
      header: "해외선물 주문 가능 금액",
      render: (r) => formatNumber(r.ord_able_amt),
    },
  ];

  const depositAssetSummaryColumns = [
    {
      key: "ovrs_futs_dps",
      header: "해외선물예수금",
      align: "right",
      render: (r) => formatNumber(r.ovrs_futs_dps),
    },
    {
      key: "abrd_futs_eval_dpstg_tot_amt",
      header: "해외선물평가예탁총금액",
      align: "right",
      render: (r) => formatNumber(r.abrd_futs_eval_dpstg_tot_amt),
    },
    {
      key: "abrd_futs_ord_able_amt",
      header: "해외선물주문가능금액",
      align: "right",
      render: (r) => formatNumber(r.abrd_futs_ord_able_amt),
    },
    {
      key: "abrd_futs_wthdw_able_amt",
      header: "해외선물인출가능금액",
      align: "right",
      render: (r) => formatNumber(r.abrd_futs_wthdw_able_amt),
    },
    {
      key: "abrd_futs_csgn_mgn",
      header: "해외선물위탁증거금액",
      align: "right",
      render: (r) => formatNumber(r.abrd_futs_csgn_mgn),
    },
    {
      key: "ovrs_futs_maint_mgn",
      header: "해외선물유지증거금",
      align: "right",
      render: (r) => formatNumber(r.ovrs_futs_maint_mgn),
    },
    {
      key: "ovrs_futs_splm_mgn",
      header: "해외선물추가증거금",
      align: "right",
      render: (r) => formatNumber(r.ovrs_futs_splm_mgn),
    },
    {
      key: "mgncl_rat",
      header: "마진콜율",
      align: "right",
      render: (r) => formatNumber(r.mgncl_rat, 1),
    },
    {
      key: "abrd_futs_lqdt_pnl_amt",
      header: "해외선물청산손익금액",
      align: "right",
      render: (r) => {
        const n = Number(r.abrd_futs_lqdt_pnl_amt);
        const cls = Number.isFinite(n) ? (n >= 0 ? "text-emerald-600" : "text-red-600") : "";
        return <span className={cls}>{formatNumber(r.abrd_futs_lqdt_pnl_amt)}</span>;
      },
    },
    {
      key: "abrd_futs_eval_pnl_amt",
      header: "해외선물평가손익금액",
      align: "right",
      render: (r) => {
        const n = Number(r.abrd_futs_eval_pnl_amt);
        const cls = Number.isFinite(n) ? (n >= 0 ? "text-emerald-600" : "text-red-600") : "";
        return <span className={cls}>{formatNumber(r.abrd_futs_eval_pnl_amt)}</span>;
      },
    },
  ];

  const depositAssetByCurrencyColumns = [
    {
      key: "CrcyCode",
      header: "통화코드",
      render: (r) => formatPlain(pick(r, ["CrcyCode"])),
    },
    {
      key: "OvrsFutsDps",
      header: "해외선물예수금",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OvrsFutsDps"])),
    },
    {
      key: "AbrdFutsCsgnMgn",
      header: "해외선물위탁증거금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsCsgnMgn"])),
    },
    {
      key: "OvrsFutsSplmMgn",
      header: "해외선물추가증거금",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OvrsFutsSplmMgn"])),
    },
    {
      key: "CustmLpnlAmt",
      header: "고객청산손익금액",
      align: "right",
      render: (r) => {
        const v = pick(r, ["CustmLpnlAmt"]);
        const n = Number(v);
        const cls = Number.isFinite(n) ? (n >= 0 ? "text-emerald-600" : "text-red-600") : "";
        return <span className={cls}>{formatNumber(v)}</span>;
      },
    },
    {
      key: "AbrdFutsEvalPnlAmt",
      header: "해외선물평가손익금액",
      align: "right",
      render: (r) => {
        const v = pick(r, ["AbrdFutsEvalPnlAmt"]);
        const n = Number(v);
        const cls = Number.isFinite(n) ? (n >= 0 ? "text-emerald-600" : "text-red-600") : "";
        return <span className={cls}>{formatNumber(v)}</span>;
      },
    },
    {
      key: "AbrdFutsCmsnAmt",
      header: "해외선물수수료금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsCmsnAmt"])),
    },
    {
      key: "AbrdFutsEvalDpstgTotAmt",
      header: "해외선물평가예탁총금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsEvalDpstgTotAmt"])),
    },
    {
      key: "Xchrat",
      header: "환율",
      align: "right",
      render: (r) => formatNumber(pick(r, ["Xchrat"]), 4),
    },
    {
      key: "FcurrRealMxchgAmt",
      header: "외화실환전금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["FcurrRealMxchgAmt"])),
    },
    {
      key: "AbrdFutsWthdwAbleAmt",
      header: "해외선물인출가능금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsWthdwAbleAmt"])),
    },
    {
      key: "AbrdFutsOrdAbleAmt",
      header: "해외선물주문가능금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsOrdAbleAmt"])),
    },
    {
      key: "FutsDueNarrvLqdtPnlAmt",
      header: "선물만기미도래청산손익금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["FutsDueNarrvLqdtPnlAmt"])),
    },
    {
      key: "FutsDueNarrvCmsn",
      header: "선물만기미도래수수료",
      align: "right",
      render: (r) => formatNumber(pick(r, ["FutsDueNarrvCmsn"])),
    },
    {
      key: "AbrdFutsLqdtPnlAmt",
      header: "해외선물청산손익금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsLqdtPnlAmt"])),
    },
    {
      key: "OvrsFutsDueCmsn",
      header: "해외선물만기수수료",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OvrsFutsDueCmsn"])),
    },
    {
      key: "OvrsFutsOptBuyAmt",
      header: "해외선물옵션매수금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OvrsFutsOptBuyAmt"])),
    },
    {
      key: "OvrsFutsOptSellAmt",
      header: "해외선물옵션매도금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OvrsFutsOptSellAmt"])),
    },
    {
      key: "OptBuyMktWrthAmt",
      header: "옵션매수시장가치금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OptBuyMktWrthAmt"])),
    },
    {
      key: "OptSellMktWrthAmt",
      header: "옵션매도시장가치금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OptSellMktWrthAmt"])),
    },
  ];

  const orderableColumns = [
    { key: "isu_code", header: "종목 코드" },
    {
      key: "ord_able_qty",
      header: "주문 가능 수량",
      render: (r) => formatNumber(r.ord_able_qty, 0),
    },
  ];

  const unsettledSummaryColumns = [
    {
      key: "deposit",
      header: "예수금",
      align: "right",
      render: (r) => formatNumber(r.deposit),
    },
    {
      key: "maintenance_margin",
      header: "유지증거금",
      align: "right",
      render: (r) => formatNumber(r.maintenance_margin),
    },
    {
      key: "margin_call_rate",
      header: "마진콜율",
      align: "right",
      render: (r) => formatNumber(r.margin_call_rate),
    },
    {
      key: "orderable_amount",
      header: "주문가능 금액",
      align: "right",
      render: (r) => formatNumber(r.orderable_amount),
    },
  ];

  const unsettledDetailColumns = [
    {
      key: "ExpDt",
      header: "만기일",
      render: (r) => formatPlain(pick(r, ["ExpDt", "MtrtDt", "DueDt", "XprDt"])),
    },
    {
      key: "BalQty",
      header: "잔고수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["BalQty", "JanQty", "Qty"]), 0),
    },
    {
      key: "PchsPrc",
      header: "매입가격",
      align: "right",
      render: (r) => formatNumber(pick(r, ["PchsPrc", "BuyPrc", "Prc", "AvgPrc"])),
    },
    {
      key: "Cmsn",
      header: "위탁수수료",
      align: "right",
      render: (r) => formatNumber(pick(r, ["Cmsn", "CmsnAmt", "Fee", "EntrustFee"])),
    },
    {
      key: "ComCdNm",
      header: "공통코드명",
      render: (r) => formatPlain(pick(r, ["ComCdNm", "CommCdNm", "CodeNm", "IsuNm", "ItemNm"])),
    },
  ];

  const orderFillDetailColumns = [
    {
      key: "OvrsFutsOrdNo",
      header: "주문 번호",
      render: (r) => formatPlain(pick(r, ["OvrsFutsOrdNo"])),
    },
    {
      key: "BnsTpNm",
      header: "거래 구분",
      render: (r) => formatPlain(pick(r, ["BnsTpNm", ",BnsTpCode", "BnsNm", "Side"])),
    },
    {
      key: "OrdSndDttm",
      header: "주문 일시",
      render: (r) => formatLsDateTime(pick(r, ["OrdSndDttm", "OrdDt"])),
    },
    {
      key: "ExecDttm",
      header: "체결 일시",
      render: (r) => formatLsDateTime(pick(r, ["ExecDttm", "ExecDt"])),
    },
    {
      key: "OrdPtnNm",
      header: "주문 유형",
      render: (r) => formatPlain(pick(r, ["OrdPtnNm", "OrdPtnCode", "OrdType", "OrdTypeNm"])),
    },
    {
      key: "OrdQty",
      header: "주문 수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["OrdQty"]), 0),
    },
    {
      key: "ExecQty",
      header: "체결 수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["ExecQty"]), 0),
    },
    {
      key: "AbrdFutsExecPrc",
      header: "체결 가격",
      align: "right",
      render: (r) => formatNumber(pick(r, ["AbrdFutsExecPrc", "ExecPrc", "ChePrc", "CcldPrc", "FillPrc"])),
    },
    {
      key: "IsuNm",
      header: "종목명",
      render: (r) => formatPlain(pick(r, ["IsuNm", "FnoIsuNm", "ItemNm", "ExecIsuNm"])),
    },
    {
      key: "UnercQty",
      header: "미체결 수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["UnercQty", "UnExecQty", "UnfilledQty"]), 0),
    },
  ];

  const dailyUnsettledColumns = [
    {
      key: "IsuCodeVal",
      header: "종목 코드",
      render: (r) => formatPlain(pick(r, ["IsuCodeVal", "IsuCode", "FnoIsuCode", "Symbol"])),
    },
    {
      key: "BalQty",
      header: "잔고 수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["BalQty", "JanQty", "Qty"]), 0),
    },
    {
      key: "LqdAbleQty",
      header: "청산 가능 수량",
      align: "right",
      render: (r) => formatNumber(pick(r, ["LqdAbleQty", "LiqAbleQty", "CloseAbleQty"]), 0),
    },
    {
      key: "BnsTpCode",
      header: "매매 구분",
      render: (r) => formatPlain(pick(r, ["BnsTpCode", "BnsTpNm", "BnsNm", "Side"])),
    },
    {
      key: "PchsPrc",
      header: "매입 가격",
      align: "right",
      render: (r) => formatNumber(pick(r, ["PchsPrc", "BuyPrc", "AvgPrc"])),
    },
    {
      key: "ExpDt",
      header: "만기일자",
      render: (r) => formatPlain(pick(r, ["ExpDt", "MtrtDt", "DueDt", "XprDt"])),
    },
    {
      key: "EvalPnlAmt",
      header: "해외선물 평가 손익 금액",
      align: "right",
      render: (r) => {
        const v = pick(r, ["EvalPnlAmt", "OvrsFutsEvalPnlAmt", "EvalPnl", "PnlAmt"]);
        const n = Number(v);
        const cls = Number.isFinite(n) ? (n >= 0 ? "text-emerald-600" : "text-red-600") : "";
        return <span className={cls}>{formatNumber(v)}</span>;
      },
    },
    {
      key: "CustBalAmt",
      header: "고객 잔고 금액",
      align: "right",
      render: (r) => formatNumber(pick(r, ["CustBalAmt", "CstmrBalAmt", "CustomerBalAmt"])),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl px-6 py-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">LS 해외선물 계좌 현황</h1>
        </div>

        <button
          onClick={fetchSummary}
          disabled={loading || cooldown}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "조회 중…" : cooldown ? "잠시 후 조회 가능" : "조회"}
        </button>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 rounded-lg border bg-gray-50 p-4 md:grid-cols-3">
        <label className="text-sm">
          <div className="mb-1 font-semibold text-gray-700">종목코드</div>
          <input
            value={isuCode}
            onChange={(e) => setIsuCode(e.target.value.toUpperCase())}
            className="w-full rounded border px-2 py-1"
            placeholder="NQ 또는 MNQZ26"
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 font-semibold text-gray-700">시작일</div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded border px-2 py-1"
          />
        </label>

        <label className="text-sm">
          <div className="mb-1 font-semibold text-gray-700">종료일</div>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded border px-2 py-1"
          />
        </label>
      </div>

      {errMsg && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errMsg}</div>
      )}

      <div className="space-y-6">
        <SectionTable title="계좌현황(CIDBQ03000)" columns={accountStatusColumns} rows={accountStatusRows} />

        <SectionTable title="주문 가능 수량(CIDBQ01400)" columns={orderableColumns} rows={orderableRows} />

        <SectionTable
          title="해외선물 예탁자산 요약(CIDBQ05300 OutBlock3)"
          columns={depositAssetSummaryColumns}
          rows={depositAssetSummaryRows}
        />

        <SectionTable
          title="해외선물 예탁자산 통화별 상세(CIDBQ05300 OutBlock2)"
          columns={depositAssetByCurrencyColumns}
          rows={depositAssetByCurrencyRows}
        />

        <SectionTable title="미결제 잔고 요약(CIDBQ01500)" columns={unsettledSummaryColumns} rows={unsettledSummary} />

        <SectionTable title="미결제 잔고내역(CIDBQ01500)" columns={unsettledDetailColumns} rows={unsettledDetailRows} />

        <SectionTable
          title="일자별 미결제 잔고내역(CIDEQ00800)"
          columns={dailyUnsettledColumns}
          rows={dailyUnsettledRows}
        />

        <SectionTable
          title="해외선물 주문체결내역 상세 조회(CIDBQ02400)"
          columns={orderFillDetailColumns}
          rows={orderFillDetailRows}
        />
      </div>

      <div className="mt-6 rounded-lg border bg-gray-50 p-4">
        <button
          onClick={() => setRawOpen((v) => !v)}
          className="rounded border bg-white px-3 py-1 text-sm hover:bg-gray-100"
        >
          {rawOpen ? "Raw 응답 숨기기" : "Raw 응답 보기"}
        </button>

        {rawOpen && (
          <pre className="mt-3 max-h-[520px] overflow-auto rounded bg-black p-3 text-xs text-green-200">
            {JSON.stringify(data?.raw || data || {}, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
